import { pageTitle } from '@palaccount/config';
import { formatApprovalLimit } from '@palaccount/contracts';
import type { PurchaseOrderResponse, TwoWayAmount } from '@palaccount/contracts';
import {
  CheckCircle2,
  Loader2,
  PackageCheck,
  Plus,
  ReceiptText,
  Send,
  ShieldAlert,
  Trash2,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router';

import { Amount, Ltr } from '@/components/amount';
import { ConfirmBox, DeleteConfirmBox } from '@/components/confirm-box';
import { ReasonBox } from '@/components/reason-box';
import { Gated } from '@/components/permission-gate';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DocumentNumberChip } from '@/components/document-number-chip';
import { useUnsavedChanges } from '@/components/document-header-controls';
import { DocumentToolbar } from '@/components/document-toolbar';
import { useLineImport } from '@/features/documents/line-tools';
import { DocumentTotals, TwoWayPair } from '@/features/documents/document-totals';
import { LastPriceHint } from '@/features/documents/last-price-hint';
import { DescriptionLangToggle, useDescriptionLanguage } from '@/features/documents/description-language';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { AttachmentsPanel } from '@/features/attachments/attachments-panel';
import { DocumentActions } from '@/features/documents/document-actions';
import { FxPanel, useRateWarningGate } from '@/features/documents/fx-panel';
import { RateField, useRateInput } from '@/features/documents/rate-field';
import { DocCurrencyCell, useDocCurrency } from '@/features/documents/doc-currency';
import { CollapsibleHeaderCard } from '@/features/documents/header-card';
import { purchaseOrderDocument } from '@/features/documents/record-documents';
import { ItemCell } from '@/features/inventory/item-cell';
import { LineUnitSelect } from '@/features/sales/sales-header-fields';
import { useItemPicker } from '@/features/inventory/item-picker';
import { usePartnerPicker } from '@/features/partners/partner-picker';
import { PartnerTypeahead } from '@/features/partners/partner-typeahead';
import { computeTotalsMirror, type TwoWayState } from '@/features/sales/totals-mirror';
import { denyPermission, useCan } from '@/lib/permissions';
import { ApiError, localizeServerCause, localizeServerMessage } from '@/lib/server-messages';
import { cn } from '@/lib/utils';
import { useBaseCurrency, useDocumentDate } from '@/lib/session';

import {
  useApprovePurchaseOrder,
  useCancelPurchaseOrder,
  useReopenPurchaseOrder,
  useCreatePurchaseOrder,
  useDeletePurchaseOrder,
  usePurchaseOrder,
  useSendPurchaseOrder,
  useUpdatePurchaseOrder,
} from './api';
import { useOrgSetting } from '@/features/admin/api';

import { billedTone, canSend, needsApproval } from './po-rules';
import { PoStatusChip } from './status-chips';

interface GridLine {
  key: number;
  itemId: string;
  /** Wave 4C: the unit the qty is typed in; '' = the item's stock unit. */
  unitId: string;
  description: string;
  qty: string;
  unitPrice: string;
  discountPct: string;
  /**
   * BR-MD-03-14's "already typed" — a fact about the CELL, set by its own
   * `onChange` and by nothing else. The reasoning, and the three cases that
   * rule out the two simpler tests ("is it empty" and "does it still hold
   * what the pick put there"), is written out once on the same field in
   * `sales/invoice-editor-page.tsx`.
   */
  typedDescription: boolean;
  typedUnitPrice: boolean;
}

let nextKey = 1;
const blankLine = (): GridLine => ({
  key: nextKey++,
  itemId: '',
  unitId: '',
  description: '',
  qty: '',
  unitPrice: '',
  discountPct: '',
  typedDescription: false,
  typedUnitPrice: false,
});

const MONEY_RE = /^\d{1,13}(\.\d{1,2})?$/;
const PCT_RE = /^\d{1,3}(\.\d{1,2})?$/;
const QTY_RE = /^\d{1,12}(\.\d{1,3})?$/;

function fromOrder(order: PurchaseOrderResponse): GridLine[] {
  return order.lines.map((line) => ({
    key: nextKey++,
    itemId: line.itemId ?? '',
    unitId: line.unitId ?? '',
    description: line.description,
    qty: line.qty,
    unitPrice: line.unitPrice,
    discountPct: line.discountPct,
    // A saved line's text and price are somebody's typed work.
    typedDescription: true,
    typedUnitPrice: true,
  }));
}

function CellInput({ mono, className, ...props }: React.ComponentProps<'input'> & { mono?: boolean }) {
  return (
    <input
      className={cn(
        'h-9 w-full min-w-0 rounded-md border border-transparent bg-transparent px-2 text-sm outline-none transition-colors',
        'hover:border-input focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/50',
        'disabled:cursor-not-allowed disabled:opacity-70',
        mono && 'text-end font-mono tabular-nums',
        className,
      )}
      {...props}
    />
  );
}

/**
 * PUR-01 on the SAL-00 shared layout — the slice's subset: the details line
 * (supplier + expected date, BR-PUR-01-10), the item-linked grid, the PART 18
 * summary, and the lifecycle rail: the approval gate with the EXACT
 * BR-PUR-01-01 sentence (amber for the permission-less, green confirmation
 * for the approver — the §15 proposed default), Send behind the gate, Cancel,
 * and Create bill (hidden on a closed/cancelled order — BR-PUR-01-08).
 * A SENT order is a commitment: read-only except its notes (BR-PUR-01-11a).
 *
 * ── NO REQUISITION PILL, AND THAT IS THE RULE BEING MET ─────────────────────
 *
 * BR-PUR-01-07: "The requisition pill must open the requisition. Either build
 * the requisition screen or remove the pill." The rule offers two repairs and
 * this build took the second — there is no purchase-requisition screen, no
 * requisition field on the order contract, and no pill on this page. (The
 * word appears elsewhere in the product for HR HIRING requisitions and for a
 * manufacturing planning suggestion; neither is this.)
 *
 * ERR-PUR-01-02 is the same fact from §14 — "Clicked the requisition pill |
 * (a message appears; nothing opens)". It is a row with no message text,
 * describing the prototype's dead control, and it stops being reachable when
 * there is no control to click.
 *
 * WHY THIS IS WRITTEN DOWN RATHER THAN LEFT AS AN ABSENCE. An absence has no
 * line to read, so the next person to see `PR-0442` in the spec's walkthrough
 * has no way to tell "deliberately removed under BR-PUR-01-07" from "not done
 * yet". Re-adding a pill that only shows a toast would re-open the rule
 * without touching any test — `pur-queue-rules.test.tsx` exists to make that
 * fail instead.
 */
export function OrderEditorPage() {
  const { t, i18n } = useTranslation();
  // The reader's language is the PDF's; `print.language` may override it in
  // the template (INV-H6), for the worker and the browser alike.
  const printLocale = i18n.language.startsWith('ar') ? ('ar' as const) : ('en' as const);
  const navigate = useNavigate();
  const { orderId } = useParams<{ orderId: string }>();
  const isNew = !orderId;

  const { data: order, isPending: loadingOrder } = usePurchaseOrder(orderId);
  const createOrder = useCreatePurchaseOrder();
  const updateOrder = useUpdatePurchaseOrder(orderId ?? '');
  const deleteOrder = useDeletePurchaseOrder(orderId ?? '');
  const approveOrder = useApprovePurchaseOrder(orderId ?? '');
  const sendOrder = useSendPurchaseOrder(orderId ?? '');
  const cancelOrder = useCancelPurchaseOrder(orderId ?? '');
  const reopenOrder = useReopenPurchaseOrder(orderId ?? '');

  const canEdit = useCan(isNew ? 'purchasing.create' : 'purchasing.edit');

  const [partnerId, setPartnerId] = useState('');
  const [orderDate, setOrderDate] = useDocumentDate();
  const [expectedDate, setExpectedDate] = useState('');
  const [lines, setLines] = useState<GridLine[]>([blankLine()]);
  const [discount, setDiscount] = useState<TwoWayState | undefined>(undefined);
  const [tax, setTax] = useState<TwoWayState | undefined>(undefined);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  /**
   * Wave 2 — what the send just did about the supplier's copy: the address a
   * message carrying the order's PDF was queued to, `null` when the supplier
   * has no address on file, `undefined` until a send has happened on this
   * screen. A send from /new navigates here with the answer in router state,
   * so the banner survives the move to the saved order's own URL.
   */
  const location = useLocation();
  const [emailedTo, setEmailedTo] = useState<string | null | undefined>(
    (location.state as { emailedTo?: string | null } | null)?.emailedTo,
  );
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState(false);
  /** BR-MD-11-14's question, in ADM-02 §8's shared box (BR-PERM-15). */
  const { confirmRateWarning, rateWarningDialog } = useRateWarningGate();
  /**
   * `window.confirm` is neither of §16.5.3's two patterns, so BR-PERM-15
   * (spec line 35168) forbade it; the chrome went first (recorded then as
   * "chrome removal only"). GEN-59's OTHER HALF IS NOW PAID TOO (spec line
   * 3806: the cancel asks for a reason AND STORES IT ON THE RECORD): the
   * contract carries the reason (purchase-orders.ts cancel body: required,
   * 1..500), the service writes `cancel_reason` on the order and the reason
   * onto the audit row (20260903100000), and the cancelled banner below
   * reads it back. Cancelling a SENT order is the legal correction of a
   * commitment — the case for a stored reason at its strongest — so the ask
   * is §8's typed-reason `ReasonBox`.
   */
  const [cancelAsk, setCancelAsk] = useState(false);
  /**
   * Wave 5E — AMEND, the other correction of a sent order. The server allows
   * it only while nothing has acted on the commitment (no posted receipt,
   * bill or import order) and names the blocking document otherwise
   * (`PO_REFUSALS.reopenBlocked`). Same §8 typed-reason box as the cancel.
   */
  const [amendAsk, setAmendAsk] = useState(false);

  /**
   * GEN-209's FIFTH ACT — "approving or rejecting a submission" — spec line
   * 4709. Approve wrote to the server on click; it now opens the question
   * first, like the delete and the cancel above it.
   *
   * ── THERE IS NO REJECT HERE, AND THAT IS THE SCREEN'S SHAPE, NOT A GAP ────
   *
   * PUR-01 gives an over-limit draft exactly one door: BR-PUR-01-01 (spec line
   * 11017) says such an order "must not be sent to the supplier without
   * approval from a user holding `exp.approve`". Withholding approval is the
   * default state, not an act — there is no reject route on the contract and
   * no rejected status among the order's six. So GEN-209's clause lands here
   * as "approving" alone, and a Reject button would be inventing a workflow
   * the specification does not describe.
   *
   * ── WHY A `ConfirmBox` AND NOT A `ReasonBox` ──────────────────────────────
   *
   * Same finding as the payments voucher, reached from PUR-01's own text:
   * BR-PUR-01-01 through -07 never mention a reason, GEN-59's reason rule is
   * about Cancel, and §16.5.3's enumeration of the typed-reason acts (spec
   * line 35165) does not include an order approval. And the contract could not
   * carry one regardless — `approve` declares
   * `body: z.object({}).strict().optional()`, the strict-empty shape the
   * four cancels ALSO had until GEN-59 widened theirs (the approve body
   * deliberately was not), which REFUSES an extra field rather than
   * dropping it.
   *
   * NOT A RULING THAT NO REASON IS EVER WANTED. If one is, it needs a contract
   * field, a service that writes it and a column — and `po-approval-asks.
   * test.tsx` asserts the refusal at runtime so that widening the contract
   * goes red here rather than leaving this paragraph describing a world that
   * has moved.
   */
  const [approveAsk, setApproveAsk] = useState(false);
  const loadedId = useRef<string | null>(null);
  /** S4 — the header rail's ‹‹ ›› wait while there is typed work (see `useUnsavedChanges`). */
  const dirty = useUnsavedChanges(
    [partnerId, orderDate, expectedDate, lines, discount, tax, notes],
    isNew ? 'new' : order && loadedId.current === `${order.id}:${order.status}` ? loadedId.current : null,
  );

  const status = order?.status ?? 'draft';
  const preCommitment = status === 'draft' || status === 'approved';
  const editable = preCommitment && canEdit;
  const terminal = status === 'closed' || status === 'cancelled';

  useEffect(() => {
    document.title = pageTitle(t('po.editorTitle'));
  }, [t]);

  useEffect(() => {
    if (order && loadedId.current !== `${order.id}:${order.status}`) {
      loadedId.current = `${order.id}:${order.status}`;
      setPartnerId(order.partnerId);
      setOrderDate(order.orderDate);
      setExpectedDate(order.expectedDate ?? '');
      setLines(fromOrder(order));
      setDiscount({ basis: 'pct', value: order.discountPct });
      setTax({ basis: 'pct', value: order.taxPct });
      setNotes(order.notes ?? '');
    }
  }, [order]);

  // GEN-50 — one panel of matches, plus the records these lines already name.
  const supplierPicker = usePartnerPicker(partnerId || null, { type: 'supplier' });
  const itemPicker = useItemPicker(
    useMemo(() => lines.map((line) => line.itemId), [lines]),
    { status: 'active' },
  );

  const supplier = supplierPicker.byId.get(partnerId);
  // GEN-108's chain, ending where it should: the DOCUMENT's own code, else
  // the party's usual currency, else the company's base (GEN-107) read from
  // the session — never a literal, which printed dollars on a euro
  // company's every new document until a party was picked.
  const base = useBaseCurrency();
  // Wave 4D — the editable rate (features/documents/rate-field).
  const rate = useRateInput(order);
  // Per-document currency (2026-10-30): the header's dropdown in front of the chain.
  const docCurrency = useDocCurrency({
    partnerId,
    savedCurrency: order?.currency,
    partnerCurrency: supplier?.currency ?? base,
    onPick: () => rate.setText(''),
  });
  const currency = docCurrency.currency;

  const defaultTaxPct = useOrgSetting<string>('invoicing.default_vat_pct');
  const totals = useMemo(
    () => computeTotalsMirror(lines, discount, tax, currency, defaultTaxPct),
    [lines, discount, tax, currency, defaultTaxPct],
  );

  // The live mirror of the gate; the SERVER re-derives on send (BR-PUR-01-01)
  // against the company's own treasury.po_approval_limit setting.
  const poLimit = useOrgSetting<string>('treasury.po_approval_limit');
  const gateTripped =
    status === 'draft' &&
    needsApproval(order?.grandTotal ?? totals.grandTotal ?? '0', currency, poLimit);

  const setLine = (key: number, patch: Partial<GridLine>) => {
    setLines((current) => current.map((line) => (line.key === key ? { ...line, ...patch } : line)));
  };

  /**
   * BR-MD-03-14: picking fills the description and the buying price, never
   * over typed work; free text stays legal.
   */
  const onPickItem = (key: number, itemId: string) => {
    const item = itemPicker.byId.get(itemId);
    if (!item) {
      setLine(key, { itemId: '' });
      return;
    }
    const price =
      item.costPrice && item.costPrice !== '0.00' ? item.costPrice : (item.unitPrice ?? '');
    setLines((current) =>
      current.map((line) =>
        line.key === key
          ? {
              ...line,
              itemId,
              // The picked unit belonged to the OLD item.
              unitId: '',
              // Per FIELD, not per line: a typed description must not stop
              // the untouched price filling.
              description: line.typedDescription ? line.description : descLang.describe(item),
              unitPrice: line.typedUnitPrice ? line.unitPrice : price,
            }
          : line,
      ),
    );
  };

  const removeLine = (key: number) => {
    setLines((current) => {
      const remaining = current.filter((line) => line.key !== key);
      return remaining.length === 0 ? [blankLine()] : remaining;
    });
  };

  const usedLines = lines.filter(
    (line) =>
      line.description.trim() !== '' || line.qty.trim() !== '' || line.unitPrice.trim() !== '',
  );

  const validate = (): string | null => {
    if (!partnerId) return t('po.errNoSupplier');
    // `po.errNoLines`, not `sales.errNoLines`: an order is SENT to a supplier
    // and an invoice is POSTED, and this local mirror has to say the same word
    // as the server door it mirrors (`PO_MESSAGES.noLines`). It said "before
    // posting" until the two were split — a purchaser reading the sales
    // invoice's door on their own screen, in Arabic as well as English.
    //
    // Worth knowing that the map fix alone did NOT reach this line: the server
    // sentence goes through `localizeServerMessage`, and this one never leaves
    // the browser. A key collision has to be chased on both sides.
    if (usedLines.length === 0) return t('po.errNoLines');
    for (const [index, line] of usedLines.entries()) {
      if (line.description.trim() === '') return t('sales.errLineDescription', { line: index + 1 });
      if (!QTY_RE.test(line.qty.trim()) || /^0+(\.0+)?$/.test(line.qty.trim()))
        return t('sales.errLineQty', { line: index + 1 });
      if (!MONEY_RE.test(line.unitPrice.trim())) return t('sales.errLinePrice', { line: index + 1 });
      const pct = line.discountPct.trim();
      if (pct !== '' && !PCT_RE.test(pct)) return t('sales.errDiscountRange');
    }
    if (discount && !(discount.basis === 'pct' ? PCT_RE : MONEY_RE).test(discount.value.trim()))
      return t('sales.errDiscountRange');
    if (tax && !(tax.basis === 'pct' ? PCT_RE : MONEY_RE).test(tax.value.trim()))
      return t('sales.errTaxRange');
    return null;
  };

  const buildTwoWay = (state: TwoWayState | undefined): TwoWayAmount | undefined => {
    if (!state) return undefined;
    return state.basis === 'pct' ? { pct: state.value.trim() } : { amount: state.value.trim() };
  };

  const requestLines = () =>
    usedLines.map((line) => ({
      ...(line.itemId ? { itemId: line.itemId } : {}),
      ...(line.itemId && line.unitId ? { unitId: line.unitId } : {}),
      description: line.description.trim(),
      qty: line.qty.trim(),
      unitPrice: line.unitPrice.trim(),
      ...(line.discountPct.trim() !== '' ? { discountPct: line.discountPct.trim() } : {}),
    }));

  type PurchPermission =
    | 'purchasing.create'
    | 'purchasing.edit'
    | 'purchasing.delete'
    | 'purchasing.approve';
  const handleFailure = (thrown: unknown, permission: PurchPermission) => {
    if (thrown instanceof ApiError && thrown.status === 403) denyPermission(permission);
    // The line above already reads the cause to spot a 403; this one used to
    // throw it away one expression later. `localizeServerCause` keeps it: a
    // refusal keeps the server's sentence localized, a request that never
    // answered gets this screen's own rather than `Failed to fetch`.
    setError(localizeServerCause(t, thrown, 'po.errSaveFailed'));
  };

  /** Wave 4C — lines from a small .xlsx/.csv, priced at the item's cost; unknown codes stay free text. */
  /** Wave 4C — which name a picked/imported item writes into a NEW line description. */
  const descLang = useDescriptionLanguage();
  const lineImport = useLineImport({
    id: 'purchase-order',
    side: 'purchase',
    describe: descLang.describe,
    copyLines: () =>
      lines
        .filter((line) => line.itemId !== '' || line.description.trim() !== '')
        .map((line) => ({
          code: itemPicker.byId.get(line.itemId)?.code ?? '',
          description: line.description,
          qty: line.qty,
          unitPrice: line.unitPrice,
          discountPct: line.discountPct,
        })),
    visible: preCommitment,
    gate: {
      permission: isNew ? 'purchasing.create' : 'purchasing.edit',
      reasonKey: isNew ? 'perm.cannot.createPurchaseOrders' : 'perm.cannot.editPurchaseOrders',
    },
    disabled: busy || !editable,
    onLines: (imported) =>
      setLines((current) => [
        ...current.filter(
        (line) =>
          line.itemId !== '' ||
          line.description.trim() !== '' ||
          line.qty.trim() !== '' ||
          line.unitPrice.trim() !== '',
      ),
        ...imported.map((line) => ({
          ...blankLine(),
          itemId: line.itemId,
          description: line.description,
          qty: line.qty,
          unitPrice: line.unitPrice,
          discountPct: line.discountPct,
          typedDescription: true,
          typedUnitPrice: true,
        })),
      ]),
  });

  const save = async (navigateAfter = true): Promise<PurchaseOrderResponse | null> => {
    setError(null);
    const problem = validate();
    if (problem) {
      setError(problem);
      return null;
    }
    try {
      if (isNew) {
        const created = await createOrder.mutateAsync({
          ...rate.createBody(),
          ...docCurrency.body(),
          partnerId,
          orderDate,
          ...(expectedDate ? { expectedDate } : {}),
          lines: requestLines(),
          ...(discount ? { discount: buildTwoWay(discount)! } : {}),
          ...(tax ? { tax: buildTwoWay(tax)! } : {}),
          ...(notes.trim() ? { notes } : {}),
        });
        if (navigateAfter) void navigate(`/purchases/orders/${created.id}`, { replace: true });
        return created;
      }
      const updated = await updateOrder.mutateAsync({
        ...rate.patchBody(),
        ...docCurrency.body(),
        orderDate,
        expectedDate: expectedDate === '' ? null : expectedDate,
        lines: requestLines(),
        ...(discount ? { discount: buildTwoWay(discount)! } : {}),
        ...(tax ? { tax: buildTwoWay(tax)! } : {}),
        notes: notes.trim() === '' ? null : notes,
      });
      loadedId.current = null;
      return updated;
    } catch (thrown) {
      handleFailure(thrown, isNew ? 'purchasing.create' : 'purchasing.edit');
      return null;
    }
  };

  const onSave = async () => {
    setBusy(true);
    await save();
    setBusy(false);
  };

  const onApprove = async () => {
    setBusy(true);
    setError(null);
    try {
      await approveOrder.mutateAsync();
      loadedId.current = null;
    } catch (thrown) {
      handleFailure(thrown, 'purchasing.approve');
    } finally {
      setBusy(false);
    }
  };

  /**
   * The commitment door. A DRAFT saves its edits first; an APPROVED order
   * sends AS APPROVED — saving would rewrite it and withdraw the approval
   * (the edit-withdraws-approval rule), which is exactly what Send must not
   * silently do.
   */
  const onSend = async () => {
    // Wave 1C — the send door's own precondition, mirrored before the round
    // trip (`PO_MESSAGES.expectedDateRequired`): a commitment carries the
    // date the late flag and the chase nudge read (BR-PUR-01-10).
    if (!canSend({ expectedDate })) {
      setError(t('po.errExpectedDateRequired'));
      return;
    }
    // BR-MD-11-14 at the confirming act — and for a purchase order that act is
    // SEND, which is where `PurchaseOrdersService.send` calls `freezeRate`.
    // Not approve: approve is an internal gate that ONLY orders above the
    // approval limit pass through at all, so an ordinary below-limit order
    // goes draft → sent and never touches it. Asking the question there would
    // have asked it about a rate not yet frozen, and — for the majority case —
    // never asked it at all.
    if (!(await confirmRateWarning(order?.rateWarning))) return;
    setBusy(true);
    setError(null);
    try {
      if (editable && status === 'draft') {
        const saved = await save(!isNew);
        if (!saved) return;
        if (isNew) {
          // The URL-bound hook is stale on /new — send by the fresh id.
          const { api } = await import('@/api');
          const response = await api.purchaseOrders.send({
            params: { orderId: saved.id },
            body: {},
          });
          if (response.status !== 200) {
            if (response.status === 403) denyPermission('purchasing.edit');
            const body = response.body;
            setError(
              localizeServerMessage(
                t,
                typeof body === 'object' && body !== null && 'message' in body
                  ? String((body as { message: unknown }).message)
                  : t('common.loadFailed'),
              ),
            );
            void navigate(`/purchases/orders/${saved.id}`, { replace: true });
            return;
          }
          void navigate(`/purchases/orders/${saved.id}`, {
            replace: true,
            state: { emailedTo: response.body.emailedTo },
          });
          return;
        }
      }
      const sent = await sendOrder.mutateAsync();
      setEmailedTo(sent.emailedTo);
      loadedId.current = null;
    } catch (thrown) {
      handleFailure(thrown, 'purchasing.edit');
    } finally {
      setBusy(false);
    }
  };

  /** GEN-59: the ReasonBox hands over the typed reason; it rides the wire. */
  const onCancelOrder = async (reason: string) => {
    if (!orderId) return;
    setCancelAsk(false);
    setBusy(true);
    setError(null);
    try {
      await cancelOrder.mutateAsync(reason);
      loadedId.current = null;
    } catch (thrown) {
      handleFailure(thrown, 'purchasing.edit');
    } finally {
      setBusy(false);
    }
  };

  /** Wave 5E: the ReasonBox hands over the typed reason; the order is a draft again. */
  const onAmendOrder = async (reason: string) => {
    if (!orderId) return;
    setAmendAsk(false);
    setBusy(true);
    setError(null);
    try {
      await reopenOrder.mutateAsync(reason);
      loadedId.current = null;
    } catch (thrown) {
      handleFailure(thrown, 'purchasing.approve');
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async () => {
    if (!orderId) return;
    setDeleting(false);
    setBusy(true);
    setError(null);
    try {
      await deleteOrder.mutateAsync();
      void navigate('/purchases');
    } catch (thrown) {
      handleFailure(thrown, 'purchasing.delete');
    } finally {
      setBusy(false);
    }
  };

  /** The one legal edit on a committed order: its notes. */
  const onSaveNotes = async () => {
    setBusy(true);
    setError(null);
    try {
      await updateOrder.mutateAsync({ notes: notes.trim() === '' ? null : notes });
      loadedId.current = null;
    } catch (thrown) {
      handleFailure(thrown, 'purchasing.edit');
    } finally {
      setBusy(false);
    }
  };

  if (!isNew && loadingOrder) {
    return <div className="h-64 animate-pulse rounded-xl bg-muted" aria-busy />;
  }

  // BR-PUR-01-04's LIMIT, not a permission: an over-limit draft cannot be sent
  // until somebody approves it. 16.7 row 3 — the button stays live and says so
  // when pressed; only the `purchasing.edit` question greys it (row 2).
  const gateAllowsSend = status === 'approved' || (status === 'draft' && !gateTripped);
  const showCreateBill =
    !isNew && (status === 'sent' || status === 'partially_received' || status === 'received');
  // Wave 1C — "Create receipt" exists while goods can still arrive (sent or
  // part-received: two containers, two GRNs), and once the order is sent it
  // is the PRIMARY act: the next thing that happens to a commitment is the
  // lorry, not the bill. Create bill stays, outlined.
  const showCreateReceipt = !isNew && (status === 'sent' || status === 'partially_received');

  return (
    <div className="grid gap-4">
      {/* BR-MD-11-14's question in ADM-02 §8's shared box. Rendered
          UNCONDITIONALLY, unlike the delete box below: this one is awaited, so
          a dialog that unmounted mid-question would leave the send hanging with
          no error anywhere. See `useRateWarningGate`. */}
      {rateWarningDialog}
      {/* ── Toolbar ────────────────────────────────────────────────────── */}
      <div className="sticky top-0 z-10 flex flex-wrap items-center gap-3 bg-background">
        <h2 className="text-lg font-bold">{t('po.editorTitle')}</h2>
        {/* BR-SAL-00-02's naming half, on the shared chip — "Order No.". */}
        <DocumentNumberChip
          label={t('po.colNo')}
          code={order?.code ?? null}
          fallback={t('po.codeNew')}
        />
        <PoStatusChip status={status} />
        {/* The toolbar's acts, DECLARED — see `document-toolbar.tsx`. */}
        <DocumentToolbar
          header={{ kind: 'purchaseOrder', id: orderId, dirty }}
          actions={[
            {
              // Not PART 4.6's Cancel row — a link back to the list. `po.
              // cancelOrder` two rows below is the act the table means.
              id: 'back',
              row: null,
              label: t('common.cancel'),
              variant: 'outline',
              to: '/purchases',
            },
            {
              // Wave 2 — the invoice's Print/PDF pair on the purchase order:
              // the same paper `send()` now mails to the supplier. Saved
              // orders only.
              id: 'printPdf',
              row: 'print',
              node:
                !isNew && order ? (
                  <DocumentActions
                    request={{ kind: 'purchase_order', locale: printLocale, subjectId: order.id }}
                    payload={{ kind: 'purchase_order', data: purchaseOrderDocument(order) }}
                  />
                ) : null,
            },
            // Wave 4C — Import lines / Download template (shared line tools).
            ...lineImport.acts,
            {
              // A purchase order never posts to the accounts (BR-PUR-01-11),
              // so its Delete boundary is being SENT (BR-PUR-01-11a), not
              // being posted: `preCommitment` is draft-or-approved.
              id: 'delete',
              row: 'delete',
              label: t('common.delete'),
              icon: <Trash2 aria-hidden />,
              variant: 'destructive',
              visible: !isNew && preCommitment,
              gate: {
                permission: 'purchasing.delete',
                reasonKey: 'perm.cannot.deletePurchaseOrders',
              },
              disabled: busy,
              onAct: () => setDeleting(true),
            },
            {
              // PART 4.6's Cancel row proper — it ends the order's validity.
              id: 'cancelOrder',
              row: 'cancel',
              label: t('po.cancelOrder'),
              variant: 'outline',
              visible:
                !isNew && !terminal && status !== 'partially_received' && status !== 'received',
              gate: {
                permission: 'purchasing.edit',
                reasonKey: 'perm.cannot.cancelPurchaseOrders',
              },
              disabled: busy,
              onAct: () => setCancelAsk(true),
            },
            {
              // Wave 5E — back to draft for a rewrite; a revised copy goes on the next send.
              id: 'amendOrder',
              row: null,
              label: t('po.amendOrder'),
              variant: 'outline',
              visible: !isNew && status === 'sent',
              gate: {
                permission: 'purchasing.approve',
                reasonKey: 'perm.cannot.amendPurchaseOrders',
              },
              disabled: busy,
              onAct: () => setAmendAsk(true),
            },
            {
              // Wave 1C — the GRN editor seeds the order from `?poId=`.
              id: 'createReceipt',
              row: null,
              label: t('po.createReceipt'),
              icon: <PackageCheck aria-hidden />,
              variant: 'default',
              visible: showCreateReceipt,
              to: `/purchases/receipts/new?poId=${orderId}`,
            },
            {
              // BR-PUR-01-08: hidden on a closed or cancelled order.
              id: 'createBill',
              row: null,
              label: t('po.createBill'),
              icon: <ReceiptText aria-hidden />,
              variant: 'outline',
              visible: showCreateBill,
              to: `/purchases/bills/new?poId=${orderId}`,
            },
            {
              id: 'save',
              row: 'save',
              label: t('common.save'),
              visible: preCommitment,
              gate: {
                permission: isNew ? 'purchasing.create' : 'purchasing.edit',
                reasonKey: isNew
                  ? 'perm.cannot.createPurchaseOrders'
                  : 'perm.cannot.editPurchaseOrders',
              },
              busy,
              onAct: () => void onSave(),
            },
          ]}
        >
          {/* GEN-58 asks its sentence in the product’s ONE shared box
              (GEN-204, GEN-209 — "deleting a draft" is first on its list).
              `window.confirm` was neither of BR-PERM-15’s two patterns.
              Inside the same guard as the button, so the dialog cannot
              outlive the condition that allows the delete. */}
          {!isNew && preCommitment ? (
            <DeleteConfirmBox
              open={deleting}
              detail={t('po.confirmDelete')}
              onCancel={() => setDeleting(false)}
              onConfirm={() => void onDelete()}
              busy={busy}
            />
          ) : null}
          {/* §8's typed-reason dialog (GEN-59). `po.confirmCancel` was
              already a question so it is GEN-205's title unchanged; the
              body carries the consequence, the undone clause and the
              stored-reason sentence. Inside the same conditional as the
              button, the convention the delete above set.
              `gen72.confirmCancel` is the verb — deliberately NOT the
              trigger's exact words (reason-box.tsx's own rule). */}
          {!isNew && !terminal && status !== 'partially_received' && status !== 'received' ? (
            <ReasonBox
              open={cancelAsk}
              title={t('po.confirmCancel')}
              prompt={t('po.confirmCancelBody')}
              reasonLabel={t('gen72.cancelReasonLabel')}
              confirmLabel={t('gen72.confirmCancel')}
              busy={busy}
              onConfirm={(reason) => void onCancelOrder(reason)}
              onCancel={() => setCancelAsk(false)}
            />
          ) : null}
          {!isNew && status === 'sent' ? (
            <ReasonBox
              open={amendAsk}
              title={t('po.confirmAmend')}
              prompt={t('po.confirmAmendBody')}
              reasonLabel={t('po.amendReasonLabel')}
              confirmLabel={t('po.confirmAmendVerb')}
              busy={busy}
              onConfirm={(reason) => void onAmendOrder(reason)}
              onCancel={() => setAmendAsk(false)}
            />
          ) : null}
        </DocumentToolbar>
      </div>

      {/* ── The lifecycle notices ──────────────────────────────────────── */}
      {status === 'sent' || status === 'partially_received' || status === 'received' ? (
        <p className="rounded-md border border-blue-300 bg-blue-50 px-4 py-3 text-sm text-blue-900 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-200">
          {t('po.sentNotice')}
        </p>
      ) : null}
      {/* Wave 2 — whether the supplier's copy went by mail. Two sentences for
          two ordinary states (MD-01 lets a supplier exist without an address):
          the green one names the address, the grey one says what to do
          instead. Neither is an error, and neither outlives a reload — the
          order's status does, and the notice above carries that. */}
      {emailedTo ? (
        <p
          role="status"
          className="rounded-md border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-200"
        >
          {t('po.sentEmailed', { address: emailedTo })}
        </p>
      ) : emailedTo === null ? (
        <p role="status" className="rounded-md border border-dashed px-4 py-3 text-sm text-muted-foreground">
          {t('po.sentNoEmail')}
        </p>
      ) : null}
      {terminal ? (
        <p className="rounded-md border border-red-300 bg-red-50/80 px-4 py-3 text-sm text-red-900 dark:border-red-800 dark:bg-red-950 dark:text-red-200">
          {/* GEN-59's stored reason, read back OFF THE RECORD. Closed orders
              and rows cancelled before the reason existed keep the terminal
              sentence. */}
          {status === 'cancelled' && order?.cancelReason != null
            ? t('po.cancelledNotice', { reason: order.cancelReason })
            : t('po.terminalNotice')}
        </p>
      ) : null}

      {/* BR-PUR-01-01 — the approval gate, drawn for everyone (§9): amber for
          the permission-less, an Approve action for the approver, and a green
          confirmation once cleared (the §15 proposed default).

          BR-PERM-13 is the same gate stated from PART 16's side, and this
          block is both of its halves: "a blocked state to anybody without
          [the approve permission]" is the amber banner, which renders for
          every reader; "an approve action to anybody with it" is the Gated
          button, live for a holder and greyed with a reason for everybody
          else. The banner is deliberately NOT hidden from the
          permission-less — 16.7 row 3.

          THE TWO SPEC SENTENCES DISAGREE and this build follows PUR-01's.
          BR-PERM-13 quotes "This order is over $25,000 and needs approval.
          Ask an administrator."; ERR-PUR-01-01 quotes the sentence rendered
          here (`po.approvalRequired`). PUR-01's wins on three grounds: it is
          the SCREEN's spec and carries an ERR id, so it is the sentence a
          message test can be written against; BR-PERM-13's version hardcodes
          $25,000, which BR-PERM-14 — five lines below it in the same fenced
          block — forbids, while PUR-01's is parameterised over the company
          setting; and "ask an administrator" misroutes, because
          purchasing.approve is not an administrator-only switch (16.4.2 gives
          the Owner and any holder). Recorded rather than reconciled: if the
          owner prefers BR-PERM-13's wording, only the string changes.

          D46: the key is ICU (`{limit}`), fed the LIVE company limit through
          the sentence's own formatter — the old `po.approvalBanner` key had
          the DEFAULT 25,000 baked into the dictionary value, so a company
          that changed its limit read a number nobody set, in both languages.
          This mirror renders the same figure the server's structured refusal
          names on the send door (PO_REFUSALS.approvalRequired). */}
      {gateTripped ? (
        <div className="flex flex-wrap items-center gap-3 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
          <ShieldAlert className="size-4 shrink-0" aria-hidden />
          <span className="me-auto">{t('po.approvalRequired', { limit: formatApprovalLimit(poLimit) })}</span>
          {/* One banner, not two. The LIMIT is why the banner is there and it
              reads the same for everybody; whether Approve is usable is the
              separate, permission question, and it greys rather than
              subtracting the button from the banner (BR-PERM-18). */}
          {!isNew ? (
            <>
              <Gated permission="purchasing.approve" reasonKey="perm.cannot.approvePurchaseOrders">
                {/* GEN-208, spec line 4707: the click OPENS the question and
                    does nothing else. */}
                <Button size="sm" onClick={() => setApproveAsk(true)} disabled={busy}>
                  {t('po.approve')}
                </Button>
              </Gated>
              {/* Inside `gateTripped && !isNew` with the button, so the dialog
                  cannot outlive the banner that justifies it: BR-PUR-01-04 —
                  "the approval check must only apply while the order is Draft"
                  — means an order that leaves Draft under a standing question
                  has nothing left to approve.

                  `po.approve` ("Approve order") is reused as the action label
                  rather than a new key: GEN-206 wants the VERB, this already
                  is one, and it is the same words as the button that raised
                  the dialog — which is the property §8 is after when it says
                  the button "repeats the verb from the title". */}
              <ConfirmBox
                open={approveAsk}
                title={t('po.confirmApproveTitle')}
                body={t('po.confirmApproveBody')}
                confirmLabel={t('po.approve')}
                busy={busy}
                onConfirm={() => {
                  setApproveAsk(false);
                  void onApprove();
                }}
                onCancel={() => setApproveAsk(false)}
              />
            </>
          ) : null}
        </div>
      ) : null}
      {status === 'approved' ? (
        <p className="flex items-center gap-2 rounded-md border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-200">
          <CheckCircle2 className="size-4 shrink-0" aria-hidden />
          {t('po.approvalCleared')}
        </p>
      ) : null}
      {/* Wave 4C — the line import's chooser, summary and per-row problems. */}
      {lineImport.panel}
      {error ? (
        <p role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
        </p>
      ) : null}

      {/* ── The details line — foldable (2026-10-30) ─────────────────────── */}
      <CollapsibleHeaderCard
        storageKey="purchaseOrder"
        className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4"
        summary={{
          partner: order?.partnerName ?? supplier?.name,
          date: orderDate,
          currency,
          code: order?.code,
          status: <PoStatusChip status={status} />,
        }}
      >
          <div className="grid gap-1.5">
            <Label htmlFor="po-date">{t('common.date')}</Label>
            <Input
              id="po-date"
              type="date"
              dir="ltr"
              value={orderDate}
              onChange={(event) => setOrderDate(event.target.value)}
              disabled={!editable}
            />
          </div>
          <div className="grid gap-1.5">
            {/* BR-PUR-01-10 — the order's own expected delivery date, with its
                own purchase-side label (the borrowed sales key, retired). */}
            <Label htmlFor="po-expected">{t('po.fExpectedDate')}</Label>
            <Input
              id="po-expected"
              type="date"
              dir="ltr"
              value={expectedDate}
              onChange={(event) => setExpectedDate(event.target.value)}
              disabled={!editable}
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="po-supplier">{t('po.fSupplier')}</Label>
            {/* GEN-50, and BR-PUR-01-12: the picker OFFERS active suppliers,
                and still DISPLAYS the one this order already names. */}
            <PartnerTypeahead
              entity="supplier"
              id="po-supplier"
              label={t('po.fSupplier')}
              value={partnerId || null}
              onChange={(id) => setPartnerId(id ?? '')}
              options={supplierPicker.options}
              selected={supplierPicker.optionFor(partnerId)}
              query={supplierPicker.query}
              onQueryChange={supplierPicker.setQuery}
              placeholder={t('po.pickSupplier')}
              emptyLabel={t('picker.noMatchingSupplier')}
              loading={supplierPicker.loading}
              disabled={!editable || !isNew}
            />
          </div>
          <div className="grid gap-1.5">
            <Label>{t('po.fSupplierId')}</Label>
            <div className="flex h-12 items-center gap-3 rounded-[11px] border border-input bg-muted/40 px-3 font-mono text-sm">
              <Ltr value={order?.partnerCode ?? supplier?.code ?? '—'} />
              <span className="ms-auto text-muted-foreground">
                <Ltr value={currency} />
              </span>
            </div>
          </div>
          {/* Per-document currency (2026-10-30): a draft order may pick its own. */}
          <DocCurrencyCell
            idPrefix="po"
            value={currency}
            onChange={docCurrency.pick}
            disabled={!editable || status !== 'draft'}
          />
          {/* Wave 4D — the editable rate (features/documents/rate-field). */}
          <RateField idPrefix="po" currency={currency} doc={order} input={rate} disabled={!editable} />
      </CollapsibleHeaderCard>

      {/* ── The item-linked grid ───────────────────────────────────────── */}
      <Card>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-11 text-center">#</TableHead>
                <TableHead className="w-44">{t('po.colItem')}</TableHead>
                <TableHead className="w-24">{t('sales.colUnit')}</TableHead>
                <TableHead className="min-w-[200px]">{t('sales.colDescription')}
                  {editable ? <DescriptionLangToggle language={descLang} /> : null}
                </TableHead>
                <TableHead className="w-24 text-end">{t('sales.colQty')}</TableHead>
                <TableHead className="w-32 text-end">{t('sales.colUnitPrice')}</TableHead>
                {/* Wave 1C: the box holds a PERCENTAGE, and the heading says so. */}
                <TableHead className="w-24 text-end">{t('po.colDiscPct')}</TableHead>
                <TableHead className="w-32 text-end">{t('sales.colTotal')}</TableHead>
                {editable ? <TableHead className="w-11" /> : null}
              </TableRow>
            </TableHeader>
            <TableBody>
              {lines.map((line, index) => {
                const view = totals.lines[index];
                return (
                  <TableRow key={line.key} className="odd:bg-muted/30">
                    <TableCell className="text-center font-mono text-muted-foreground">
                      {index + 1}
                    </TableCell>
                    <TableCell>
                      {/* Item-linked lines: the picker fills the row; free text
                          stays legal (item_id nullable + description fallback). */}
                      <ItemCell
                        label={t('po.cellItem', { line: index + 1 })}
                        clearLabel={t('po.itemFree')}
                        picker={itemPicker}
                        value={line.itemId}
                        onPick={(itemId) => onPickItem(line.key, itemId)}
                        disabled={!editable}
                      />
                      {/* Wave 4C — the supplier's last price for this item, one click to use. */}
                      {partnerId && line.itemId ? (
                        <LastPriceHint
                          side="purchase"
                          partnerId={partnerId}
                          itemId={line.itemId}
                          currency={currency}
                          currentPrice={line.unitPrice}
                          editable={editable}
                          line={index + 1}
                          onUse={(price) => setLine(line.key, { unitPrice: price, typedUnitPrice: true })}
                        />
                      ) : null}
                    </TableCell>
                    <TableCell>
                      {/* Wave 4C — the unit the qty is typed in; '' = the item's stock unit. */}
                      <LineUnitSelect
                        item={line.itemId ? itemPicker.byId.get(line.itemId) : undefined}
                        value={line.unitId}
                        onChange={(unitId) => setLine(line.key, { unitId })}
                        disabled={!editable}
                        label={t('sales.cellUnit', { line: index + 1 })}
                        use="buy"
                      />
                    </TableCell>
                    <TableCell>
                      <CellInput
                        aria-label={t('sales.cellDescription', { line: index + 1 })}
                        value={line.description}
                        onChange={(event) =>
                          setLine(line.key, {
                            description: event.target.value,
                            typedDescription: true,
                          })
                        }
                        disabled={!editable}
                      />
                    </TableCell>
                    <TableCell>
                      <CellInput
                        mono
                        dir="ltr"
                        inputMode="decimal"
                        aria-label={t('sales.cellQty', { line: index + 1 })}
                        value={line.qty}
                        onChange={(event) => setLine(line.key, { qty: event.target.value })}
                        disabled={!editable}
                      />
                    </TableCell>
                    <TableCell>
                      <CellInput
                        mono
                        dir="ltr"
                        inputMode="decimal"
                        aria-label={t('sales.cellUnitPrice', { line: index + 1 })}
                        value={line.unitPrice}
                        onChange={(event) =>
                          setLine(line.key, {
                            unitPrice: event.target.value,
                            typedUnitPrice: true,
                          })
                        }
                        disabled={!editable}
                      />
                    </TableCell>
                    <TableCell>
                      <CellInput
                        mono
                        dir="ltr"
                        inputMode="decimal"
                        aria-label={t('sales.cellDisc', { line: index + 1 })}
                        value={line.discountPct}
                        placeholder="0"
                        onChange={(event) => setLine(line.key, { discountPct: event.target.value })}
                        disabled={!editable}
                      />
                    </TableCell>
                    <TableCell className="text-end">
                      {view?.lineTotal !== null && view?.lineTotal !== undefined ? (
                        <Amount value={view.lineTotal} currency={currency} />
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    {editable ? (
                      <TableCell>
                        <button
                          type="button"
                          aria-label={t('sales.removeLine', { line: index + 1 })}
                          title={t('sales.removeLine', { line: index + 1 })}
                          onClick={() => removeLine(line.key)}
                          className="flex size-8 items-center justify-center rounded-md text-destructive transition-colors hover:bg-destructive/10"
                        >
                          <X className="size-4" aria-hidden />
                        </button>
                      </TableCell>
                    ) : null}
                  </TableRow>
                );
              })}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell colSpan={4}>
                  {editable ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="border-dashed"
                      onClick={() => setLines((current) => [...current, blankLine()])}
                    >
                      <Plus aria-hidden /> {t('sales.addLine')}
                    </Button>
                  ) : null}
                </TableCell>
                <TableCell className="text-end">
                  <span className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground">
                    {t('sales.qtyTotal')}
                  </span>{' '}
                  <bdi dir="ltr" className="font-mono font-semibold">
                    {totals.qtyTotal}
                  </bdi>
                </TableCell>
                <TableCell colSpan={editable ? 4 : 3} className="text-end">
                  <span className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground">
                    {t('sales.subtotal')}
                  </span>{' '}
                  <Amount value={totals.subtotal} currency={currency} className="font-semibold" />
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </CardContent>
      </Card>

      {/* ── Notes + summary rail ───────────────────────────────────────── */}
      <div className="grid gap-4 lg:grid-cols-[1fr_380px]">
        <Card>
          <CardHeader>
            <CardTitle>{t('sales.notes')}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3">
            <Textarea
              aria-label={t('sales.notes')}
              placeholder={t('po.notesPh')}
              maxLength={2000}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              disabled={!editable && !(status !== 'draft' && !isNew && canEdit && !terminal)}
            />
            {!preCommitment && !terminal ? (
              <Gated
                permission="purchasing.edit"
                reasonKey="perm.cannot.editNotes"
                className="justify-self-start"
              >
                <Button variant="outline" onClick={() => void onSaveNotes()} disabled={busy}>
                  {t('sales.saveNotes')}
                </Button>
              </Gated>
            ) : null}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="grid gap-3">
            {/* Wave 4C — the shared totals rows (F-2…F-7), with the currency code by the total. */}
            <DocumentTotals
              currency={currency}
              subtotal={totals.subtotal}
              discountAmount={totals.discountAmount}
              discountControl={
                <TwoWayPair
                  kind="discount"
                  state={discount}
                  onChange={setDiscount}
                  pct={totals.discountPct ?? '0.00'}
                  amount={totals.discountAmount ?? ''}
                  disabled={!editable}
                />
              }
              taxAmount={totals.taxAmount}
              vatControl={
                <TwoWayPair
                  kind="vat"
                  state={tax}
                  onChange={setTax}
                  pct={totals.taxPct ?? '15'}
                  amount={totals.taxAmount ?? ''}
                  disabled={!editable}
                />
              }
              grandTotal={totals.grandTotal}
            />

            {/* The three-figure strip: ordered / received / billed. The GRN
                leg is Σ ACCEPTED value at the order's prices (BR-PUR-02-06);
                it draws dashed only while no receipt has posted.

                Wave 1C — ONE BASIS PER PAIR, SAID OUT LOUD. The order's grand
                total includes VAT; the received leg is Σ line value EXCLUDING
                VAT (a receipt carries no tax — "tax belongs to the bill"). The
                first build put the two side by side unlabelled, so a fully
                received order read as under-received by exactly the VAT. The
                strip now pairs received with the order's own excl.-VAT figure
                (`orderedNet`) and keeps the incl.-VAT total beside billed,
                each heading naming its basis. */}
            {order ? (
              <div className="grid grid-cols-2 gap-2 text-center text-sm sm:grid-cols-4">
                <div className="rounded-md bg-muted/60 px-2 py-2">
                  <div className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground">
                    {t('po.stripOrderedNet')}
                  </div>
                  <Amount value={order.orderedNet} currency={currency} className="font-semibold" />
                </div>
                {order.receivedTotal !== null ? (
                  <div className="rounded-md bg-muted/60 px-2 py-2">
                    <div className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground">
                      {t('po.stripReceivedNet')}
                    </div>
                    <Amount
                      value={order.receivedTotal}
                      currency={currency}
                      className="font-semibold"
                    />
                  </div>
                ) : (
                  <div
                    className="rounded-md border border-dashed border-muted-foreground/40 px-2 py-2"
                    title={t('po.receivedPending')}
                  >
                    <div className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground">
                      {t('po.stripReceivedNet')}
                    </div>
                    <span className="font-mono text-muted-foreground">—</span>
                  </div>
                )}
                <div className="rounded-md bg-muted/60 px-2 py-2">
                  <div className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground">
                    {t('po.stripOrderedGross')}
                  </div>
                  <Amount value={order.grandTotal} currency={currency} className="font-semibold" />
                </div>
                <div className="rounded-md bg-muted/60 px-2 py-2">
                  <div className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground">
                    {t('po.stripBilledGross')}
                  </div>
                  {/* Wave 1C: green only when billed EQUALS ordered; amber,
                      with the word, when more was billed than ordered. */}
                  <Amount
                    value={order.billedTotal}
                    currency={currency}
                    className={cn(
                      'font-semibold',
                      billedTone(order) === 'full' && 'text-emerald-700 dark:text-emerald-400',
                      billedTone(order) === 'over' && 'text-amber-700 dark:text-amber-400',
                    )}
                  />
                  {billedTone(order) === 'over' ? (
                    <div className="text-xs font-semibold text-amber-700 dark:text-amber-400">
                      {t('po.overBilled')}
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}

            {/* BR-SAL-04-09 names this strip by name: "The sales strip already
                does this; the three purchase strips do not, and must be
                brought into line." This is the first of the three. Hidden
                entirely for a base-currency order (GEN-116). */}
            <FxPanel doc={order} />

            {/* Send — the commitment door, gate-aware (BR-PUR-01-01/-04). */}
            {preCommitment ? (
              <Gated
                permission="purchasing.edit"
                reasonKey="perm.cannot.sendPurchaseOrders"
                className="w-full"
              >
                <Button
                  className="h-[46px] w-full bg-emerald-700 text-white hover:bg-emerald-800"
                  onClick={() => void onSend()}
                  disabled={busy || !partnerId || usedLines.length === 0 || !gateAllowsSend}
                >
                  {busy ? <Loader2 className="animate-spin" aria-hidden /> : <Send aria-hidden />}
                  {t('po.send')}
                </Button>
              </Gated>
            ) : null}
          </CardContent>
        </Card>
      </div>
      {/* Wave 2 — INV-H5's files panel on this document. Hidden
          behind "save first" on a new draft; permission follows the record. */}
      <AttachmentsPanel kind="purchase_order" entityId={orderId} />
    </div>
  );
}
